/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Chaitanya
*/

import React, { useState, useEffect, useMemo } from 'react';
import { useRoute } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
} from 'react-native';
import { formatISO, addDays, endOfDay, startOfDay } from 'date-fns';

import Image from '@components/global/Image';
import * as Layout from '@components/screen/Layout';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import SociollaText from '@components/sociolla/SociollaText';
import DatePicker from '@components/form/DatePicker';
import StatusPicker from '@components/form/StatusPicker';
import StatusBadge from '@components/global/StatusBadge';
import SelectBox from '@components/form/SelectBox';
import Button from '@components/form/Button';
import ModalFull from '@components/modal/ModalFull';

import { isWeb, fullWidth, isMobileWeb, isAndroid } from '@global/screens';
import {
  parseDate,
  currency,
  hasNoAccess,
  exportCsvApi,
  formatSkpType,
} from '@global/helpers';
import Colors from '@global/colors';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { filterAdded, paramsReseted } from '@/store/params';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetSkpInternalListQuery,
  useGetSkpInternalCountQuery,
  useGetSkpInternalInfiniteListInfiniteQuery,
} from '@api/listApi';

const dimension = Dimensions.get('screen');

const tableStructure = {
  skp_reference: {
    head: 'SKP Name',
    body: 'name',
  },
  reference_code: {
    head: 'SKP number',
    body: 'reference_code',
  },
  cost_type: {
    head: 'COA',
    body: 'coa_tax.cost_type',
  },
  skp_type: {
    head: 'Type',
    body: 'skp_type',
  },
  period: {
    head: 'Periode',
    body: 'period',
  },
  total_estimate_budget: {
    head: 'Total Estimate Budget',
    body: 'total_estimate_budget',
  },
  total_actual_budget: {
    head: 'Total Actual Budget',
    body: 'total_actual_budget',
  },
  budget_used: {
    head: 'Budget Used',
    body: 'budget_used',
  },
  budget_remaining: {
    head: 'Budget Remaining',
    body: 'budget_remaining',
  },
  status: {
    head: 'Status',
    body: 'status',
    type: 'status',
  },
  is_active: {
    head: 'Is Active',
    body: 'is_active',
    type: 'status',
  },
  last_updated_by: {
    head: 'Last Updated, By',
    body: 'last_updated_by',
  },
};

// UI filter shape (as stored in scoped params) → API query filter shape.
// Mirrors the old finalParam() transform.
const toApiFilter = (rawFilter = {}) => {
  const filter = {};
  Object.keys(rawFilter).forEach((key) => {
    if (key === 'keyword') {
      const stored = rawFilter[key];
      const keyword = stored?.$regex ?? stored;
      if (keyword) {
        filter.keyword = keyword;
      }
    } else if (['from_date', 'to_date', 'status'].includes(key)) {
      filter[key] = rawFilter[key];
    } else if (['sales_team', 'coa_taxes'].includes(key)) {
      const ids = (rawFilter[key] || []).map((el) => el.id);
      if (key === 'coa_taxes') {
        filter['coa_tax.id'] = { $in: ids };
      } else {
        filter['sales_team.id'] = { $in: ids };
      }
    } else if (key === 'skp_type') {
      filter.skp_type = rawFilter[key]?.toLowerCase();
    }
  });
  return filter;
};

// Display-formatted row. RTK cache objects are frozen, so build a new object
// rather than mutating.
const mapRow = (item) => {
  const next = { ...item };
  if (item._id) {
    next.id = item._id;
  }
  if (item.from_date && item.to_date) {
    next.period = `${parseDate(item.from_date)} - ${parseDate(item.to_date)}`;
  }
  next.total_estimate_budget = item.total_estimate_budget
    ? currency(item.total_estimate_budget, true)
    : currency(0, true);
  next.total_actual_budget = item.total_actual_budget
    ? currency(item.total_actual_budget, true)
    : currency(0, true);
  next.budget_remaining = item.budget_remaining
    ? currency(item.budget_remaining, true)
    : currency(0, true);
  next.budget_used = item.budget_used
    ? currency(item.budget_used, true)
    : currency(0, true);
  next.is_active = item.is_active ? 'active' : 'in_active';
  const lastUpdatedAt = item.updated_at ? parseDate(item.updated_at) : '-';
  const updatedBy = item.updated_by?.name || '-';
  next.last_updated_by = `${lastUpdatedAt}, ${updatedBy}`;
  next.skp_type = formatSkpType(item.skp_type);
  return next;
};

const SKP_EXPORT_ACCESS_ROLES = [
  'superadmin',
  'general_manager_marketing',
  'vice_president',
  'general_manager',
  'distributor_manager',
  'regional_manager',
  'regional_support',
  'key_account_manager',
  'group_key_account_manager',
  'national_key_account_manager',
  'branch_head',
  'sales_admin',
  'sales_supervisor',
  'salesman',
  'finance_supervisor',
  'vice_president_finance',
  'budget_controller',
  'finance_head',
  'tax_support',
  'marketing_support',
  'marketing_admin',
  'billing_supervisor',
  'chief_of_staff',
  'general',
  'regional_distributor_manager',
];

const statuses = [
  { name: 'Approved', value: 'approved' },
  { name: 'Rejected', value: 'rejected' },
  { name: 'Draft', value: 'draft' },
  { name: 'Pending Approval', value: 'need_approval' },
  { name: 'Archived', value: 'archived' },
];

const SKP_TYPE_EXPORT = [
  {
    value: 'all',
    name: 'All',
  },
  {
    value: 'mt',
    name: 'MT',
  },
  {
    value: 'gt',
    name: 'GT',
  },
  {
    value: 'subdistributor',
    name: 'Sub Distributor',
  },
];

const SKP_TYPE = [
  {
    value: 'mt',
    name: 'MT',
  },
  {
    value: 'gt',
    name: 'GT',
  },
  {
    value: 'subdistributor',
    name: 'Sub Distributor',
  },
];

const SkpInternalScreen = (props) => {
  // ── selectors ──
  const dispatch = useDispatch();
  const route = useRoute(); // for route.key when opening the detail
  const showSkpExport = useSelector((state) => state.modal.showSkpExport);
  const showFilter = useSelector((state) => state.modal.showFilter);

  // ── local state ──
  // Staged filter draft for the modal — seeded from the applied filter on open
  // so reopening keeps current selections (change one field without redoing all).
  const [draftFilter, setDraftFilter] = useState({});
  const [exporting, setExporting] = useState(false);
  const [filterExport, setFilterExport] = useState({
    current_status: '',
    from_date: '',
    to_date: '',
    skp_type: '',
    coa_taxes: '',
  });
  const [coaTaxes, setCoaTaxes] = useState([]);
  const [coaTaxesLoading, setCoaTaxesLoading] = useState(false);
  const [coaTaxesParams, setCoaTaxesParams] = useState({
    skip: 0,
    limit: 10,
    loading: false,
  });

  // ── derived ──
  const getCoaTaxSettings = useApi(MsOrder.getCoaTaxSettings);
  const hasSkpExportAccess = !hasNoAccess(SKP_EXPORT_ACCESS_ROLES);

  // ── list data ──
  // List fetch + focus lifecycle. The hook owns scope + params (single source);
  // the Mongo↔ES switch is baked into the endpoint, the UI→API filter transform
  // is supplied via toFilter.
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetSkpInternalListQuery,
    countHook: useGetSkpInternalCountQuery,
    infiniteHook: useGetSkpInternalInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  // Current search text, derived from the applied keyword filter
  const searchValue = useMemo(() => {
    const k = params.filter?.keyword;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  // Display-formatted rows (web = flat array; mobile = flattened infinite pages).
  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map(mapRow);
  }, [list.data]);

  // ── effects ──
  useEffect(() => {
    if (showFilter) {
      setDraftFilter(params.filter || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  // ── handlers ──
  const goToDetail = (data) => {
    openChild('SkpInternalDetailScreen', {
      titleScreen: data.name ? data.name : '-',
      name: data.name ? data.name : '-',
      id: data._id,
      go_back_key: route.key,
    });
  };

  // Staged filter handlers — update the local draft only; committed on Apply.
  const filterHandler = (values, field) => {
    setDraftFilter((prev) => ({ ...prev, [field]: values }));
  };

  const dateRangeHandler = (value, type) => {
    const date = new Date(value);
    if (type == 'from_date') {
      const startDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );
      setDraftFilter((prev) => ({
        ...prev,
        from_date: { $gte: startDate.toISOString() },
      }));
    } else if (type == 'to_date') {
      const endDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        23,
        59,
        59,
      );
      setDraftFilter((prev) => ({
        ...prev,
        to_date: { $lte: endDate.toISOString() },
      }));
    }
  };

  const getCoaTaxes = async ({ val = null, skip = 0 }) => {
    if (skip == 0) {
      setCoaTaxesLoading(true);
    }
    setCoaTaxesParams({ ...coaTaxesParams, loading: skip > 0, skip: skip });
    const payload = {
      params: {
        filter: {},
        skip: skip,
        limit: coaTaxesParams.limit,
      },
    };

    if (val) {
      payload.params.filter.cost_type = { $regex: val, $options: 'i' };
    }
    const response = await getCoaTaxSettings.request(payload);
    if (response.status == 200 && response?.data?.data?.length) {
      const old_result = JSON.parse(JSON.stringify(coaTaxes));
      const result = response.data.data.map((item) => {
        if (item._id) {
          item.id = item._id;
        }
        return item;
      });
      let finalResult = result;
      if (skip == 0) {
        result.unshift({ id: 'all', cost_type: 'All' });
        finalResult = result;
      } else {
        finalResult = old_result.concat(result);
      }
      setCoaTaxes(finalResult);
      if (skip > 0) {
        setCoaTaxesParams({ ...coaTaxesParams, loading: false, skip: skip });
      }
    } else {
      if (skip <= 0) {
        setCoaTaxes([]);
      }
      setCoaTaxesParams({ ...coaTaxesParams, loading: false, skip: skip });
    }
    setCoaTaxesLoading(false);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      // if (!filterExport.current_status) {
      //   dispatch(
      //     showAlert({
      //       show: true,
      //       title: 'Error',
      //       description: 'Filter status must be filled',
      //       isConfirm: false,
      //     }),
      //   );
      //   setExporting(false);
      //   return;
      // }
      if (!filterExport.skp_type) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Type status must be filled',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }
      if (!filterExport.from_date || !filterExport.to_date) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter date must be filled',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }
      if (!filterExport.coa_taxes?.length) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter COA must be filled',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }

      const hasAll = filterExport.coa_taxes?.some((coa) => coa.id === 'all');

      const coaIds = filterExport.coa_taxes
        .filter((coa) => coa.id !== 'all')
        .map((coa) => coa.id);

      const payload = {
        status: filterExport.current_status.value,
        from_date: { $gte: filterExport.from_date },
        to_date: { $lte: filterExport.to_date },
      };

      if (!hasAll && coaIds.length > 0) {
        payload['coa_tax.id'] = {
          $in: coaIds,
        };
      }

      if (filterExport.skp_type.value != 'all') {
        payload['skp_type'] = filterExport.skp_type.value;
      }
      await exportCsvApi('skp-internals', payload, {
        isDownload: false,
        message: 'Export in progress, the result will be sent to your email.',
      });
      setExporting(false);
    } catch (error) {
      console.log('error-export', error);
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'A system error occurred',
          isConfirm: false,
        }),
      );
      setExporting(false);
    }
  };

  // ── render ──
  return (
    <Layout.Container testID="skp-internal-list-screen">
      {isWeb ? (
        <Layout.Title testID="skp-internal-list-title" title="SKP Internal">
          {hasSkpExportAccess ? (
            <Button
              testID="skp-internal-list-export-btn"
              type="primary"
              rounded={true}
              buttonStyle={{ width: 90, marginRight: 10 }}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  Export
                </SociollaText>
              }
              onPress={() => {
                setFilterExport({
                  from_date: '',
                  to_date: '',
                  coa_taxes: '',
                  sales_team: '',
                });
                dispatch(
                  showPopup({
                    name: 'showSkpExport',
                    value: true,
                  }),
                );
              }}
            />
          ) : (
            false
          )}
        </Layout.Title>
      ) : (
        false
      )}
      {isWeb ? (
        <Table
          testID="skp-internal-list-table"
          name="skp-internal"
          filterName={scope}
          structures={tableStructure}
          loading={list.isLoading}
          refetching={list.isFetching}
          data={rows}
          searchKey="keyword"
          searchPlaceholder="Search by SKP Name, SKP Number"
          searchValue={searchValue}
          searchWidth={360}
          hasNoHide={true}
          hasCallback={true}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          onClickRow={({ data }) => goToDetail(data)}
        />
      ) : (
        <TableMobile
          testID="skp-internal-list-table-mobile"
          filterName={scope}
          searchKey="keyword"
          placeholder="Search by SKP Name, SKP Number"
          searchValue={searchValue}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              testID={`skp-internal-list-item-${index}`}
              activeOpacity={0.6}
              style={styles.skpItem}
              onPress={() => goToDetail(data)}
              key={index}>
              <View
                style={[styles.skpItemSection, { alignItems: 'flex-start' }]}>
                <SociollaText
                  type="medium"
                  style={{
                    fontSize: 15,
                    width: fullWidth - 140,
                    marginTop: 1,
                  }}>
                  {data.name ? data.name : '-'}
                </SociollaText>
                <StatusBadge status={data.status} />
              </View>
              <View
                style={[
                  styles.skpItemSection,
                  { alignItems: 'flex-start', marginBottom: 4 },
                ]}>
                <SociollaText
                  style={{
                    marginTop: 12,
                    marginBottom: 10,
                    fontSize: 14,
                    color: Colors.grey2,
                  }}>
                  {data.reference_code ? data.reference_code : '-'}
                </SociollaText>
                <SociollaText
                  type="medium"
                  style={{
                    marginTop: 13,
                    marginBottom: 10,
                    fontSize: 12,
                    color: Colors.blue1,
                  }}>
                  {data.total_estimate_budget}
                </SociollaText>
              </View>
              <View
                style={[
                  styles.skpItemSection,
                  { alignItems: 'flex-start', marginBottom: 4 },
                ]}>
                <View style={styles.skpItemSection}>
                  <View style={{ flexDirection: 'row' }}>
                    <Image
                      style={{
                        width: 20,
                        height: 20,
                        bottom: 3,
                        marginRight: 5,
                      }}
                      svg={true}
                      height={20}
                      width={20}
                      source={require('@assets/img/icons/menu/calendar.svg')}
                    />
                    <SociollaText style={{ fontSize: 10, color: Colors.grey2 }}>
                      {data.period ? data.period : '-'}
                    </SociollaText>
                  </View>
                </View>
                <SociollaText
                  type="medium"
                  style={{
                    marginBottom: 10,
                    fontSize: 12,
                    color: Colors.blue1,
                  }}>
                  {data?.budget_remaining ? data?.budget_remaining : '-'}
                </SociollaText>
              </View>
            </TouchableOpacity>
          )}
        />
      )}
      {/* Filter Container */}
      <Layout.Filter
        testID="skp-internal-list-filter"
        name="skp-internal"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Periode
          </SociollaText>
          <View style={{ width: '100%', flexDirection: 'row' }}>
            <DatePicker
              testID="skp-internal-list-filter-start-date"
              label="Start Date"
              name="from_date"
              style={{
                marginRight: isWeb ? 10 : 0,
                width: '50%',
              }}
              disableAfterDate={draftFilter.to_date?.$lte}
              value={draftFilter.from_date?.$gte}
              onChange={(value) => dateRangeHandler(value, 'from_date')}
            />
            <DatePicker
              testID="skp-internal-list-filter-end-date"
              label="End Date"
              name="to_date"
              style={{ width: '50%' }}
              value={draftFilter.to_date?.$lte}
              disableBeforeDate={draftFilter.from_date?.$gte}
              onChange={(value) => dateRangeHandler(value, 'to_date')}
            />
          </View>
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Status
          </SociollaText>
          <StatusPicker
            testID="skp-internal-list-filter-status"
            label=""
            options={statuses}
            value={draftFilter['status']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) => filterHandler(val.value, 'status')}
          />
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            COA
          </SociollaText>
          <SelectBox
            label={'COA'}
            editable={true}
            hideLabel
            options={coaTaxes}
            multiple={true}
            value={draftFilter?.coa_taxes}
            placeholder="Select COA"
            trackBy="id"
            labelKey="cost_type"
            formLabelKey="cost_type"
            loading={coaTaxesLoading}
            withCallback={true}
            withLoadMore
            limit={coaTaxesParams.limit}
            loadMoreEmpty={coaTaxesParams.skip > coaTaxes?.length}
            loadingCallback={coaTaxesParams.loading}
            onLoadMore={(keyword) => {
              getCoaTaxes({
                val: keyword,
                skip: coaTaxesParams.skip + coaTaxesParams.limit,
              });
            }}
            searchCallback={(keyword) => {
              getCoaTaxes({
                val: keyword,
              });
            }}
            onReady={() => {
              getCoaTaxes({});
            }}
            onChange={(value) => {
              filterHandler(value, 'coa_taxes');
            }}
          />
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Type
          </SociollaText>
          <StatusPicker
            testID="skp-internal-list-filter-type"
            label=""
            options={SKP_TYPE}
            value={draftFilter['skp_type']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) => filterHandler(val.value, 'skp_type')}
          />
        </Layout.FilterItem>
      </Layout.Filter>
      <ModalFull
        testID="skp-internal-export-modal"
        title="Export SKP Internal"
        noTranslate={true}
        show={showSkpExport}
        onClose={() => {
          setFilterExport({
            from_date: '',
            to_date: '',
            coa_taxes: '',
            sales_team: '',
          });
          dispatch(
            showPopup({
              name: 'showSkpExport',
              value: false,
            }),
          );
        }}>
        <View
          style={{
            maxHeight: isWeb
              ? '70vh'
              : dimension.height - (isAndroid ? 140 : 110),
          }}>
          <ScrollView contentContainerStyle={{ width: '100%' }}>
            <Layout.FormWrapper style={{ padding: 15 }}>
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Date
                </SociollaText>
                <View
                  style={{
                    width: '100%',
                    flexDirection: isWeb ? 'row' : 'column',
                    justifyContent: 'space-between',
                  }}>
                  <DatePicker
                    testID="skp-internal-export-start-date"
                    label="Start Date"
                    name="created_at"
                    style={{ width: isWeb ? '49%' : '100%' }}
                    value={filterExport.from_date}
                    onChange={(value) => {
                      setFilterExport({
                        ...filterExport,
                        from_date: formatISO(startOfDay(value)),
                      });
                    }}
                  />
                  <DatePicker
                    testID="skp-internal-export-end-date"
                    label="End Date"
                    name="created_at"
                    style={{ width: isWeb ? '49%' : '100%' }}
                    value={filterExport.to_date}
                    disableBeforeDate={
                      filterExport.from_date
                        ? filterExport.from_date
                        : addDays(new Date(), 1)
                    }
                    onChange={(value) => {
                      setFilterExport({
                        ...filterExport,
                        to_date: formatISO(endOfDay(value)),
                      });
                    }}
                  />
                </View>
              </Layout.FormGroup>
              <Layout.FormGroup>
                <StatusPicker
                  testID="skp-internal-export-status"
                  label="Status"
                  options={statuses}
                  value={filterExport.current_status}
                  trackBy="value"
                  labelKey="name"
                  minWidth={80}
                  onChange={(val) => {
                    setFilterExport({
                      ...filterExport,
                      current_status: val,
                    });
                  }}
                />
              </Layout.FormGroup>
              <Layout.FormGroup>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Type
                </SociollaText>
                <StatusPicker
                  testID="skp-internal-export-type"
                  label=""
                  options={SKP_TYPE_EXPORT}
                  value={filterExport.skp_type}
                  trackBy="value"
                  labelKey="name"
                  minWidth={80}
                  onChange={(val) =>
                    setFilterExport({
                      ...filterExport,
                      skp_type: val,
                    })
                  }
                />
              </Layout.FormGroup>
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  COA
                </SociollaText>
                <SelectBox
                  testID="skp-internal-export-coa"
                  label={'COA'}
                  editable={true}
                  hideLabel
                  options={coaTaxes}
                  multiple={true}
                  value={filterExport.coa_taxes}
                  placeholder="Select COA"
                  trackBy="id"
                  labelKey="cost_type"
                  formLabelKey="cost_type"
                  loading={coaTaxesLoading}
                  withCallback={true}
                  withLoadMore
                  limit={coaTaxesParams.limit}
                  loadMoreEmpty={coaTaxesParams.skip > coaTaxes?.length}
                  loadingCallback={coaTaxesParams.loading}
                  onLoadMore={(keyword) => {
                    getCoaTaxes({
                      val: keyword,
                      skip: coaTaxesParams.skip + coaTaxesParams.limit,
                    });
                  }}
                  searchCallback={(keyword) => {
                    getCoaTaxes({
                      val: keyword,
                    });
                  }}
                  onReady={() => {
                    getCoaTaxes({});
                  }}
                  onChange={(value) => {
                    const hasAll = value?.some((coa) => coa.id === 'all');
                    if (hasAll) {
                      setFilterExport({
                        ...filterExport,
                        coa_taxes: [{ id: 'all', cost_type: 'All' }],
                      });
                    } else {
                      setFilterExport({ ...filterExport, coa_taxes: value });
                    }
                  }}
                />
              </Layout.FormGroup>
            </Layout.FormWrapper>
          </ScrollView>
        </View>
        <View
          style={{
            width: '100%',
            flexDirection: 'row',
            alignItems: 'flex-end',
            justifyContent: isWeb ? 'end' : 'space-between',
            paddingVertical: isWeb ? 20 : 15,
            paddingHorizontal: isWeb ? 20 : 15,
            backgroundColor: Colors.white,
            ...(!isWeb
              ? {
                  position: isMobileWeb ? 'fixed' : 'absolute',
                  bottom: 0,
                  right: 0,
                  left: 0,
                }
              : {}),
          }}>
          <Button
            testID="skp-internal-export-cancel-btn"
            rounded={true}
            type="primary"
            buttonStyle={{
              width: isWeb
                ? 100
                : dimension.width / 2 - (isMobileWeb ? 20 : 25),
              backgroundColor: Colors.white,
              marginRight: 10,
            }}
            title={
              <SociollaText type="medium" style={{ fontSize: 14 }}>
                Cancel
              </SociollaText>
            }
            onPress={() => {
              setFilterExport({
                from_date: '',
                to_date: '',
                coa_taxes: '',
                sales_team: '',
              });
              dispatch(
                showPopup({
                  name: 'showSkpExport',
                  value: false,
                }),
              );
            }}
          />
          <Button
            testID="skp-internal-export-submit-btn"
            rounded={true}
            type="primary"
            buttonStyle={{
              width: isWeb
                ? 100
                : dimension.width / 2 - (isMobileWeb ? 20 : 25),
            }}
            disabledStyle={{ width: 90 }}
            disabled={exporting}
            title={
              <SociollaText
                type="medium"
                style={{ fontSize: 14, color: Colors.white }}>
                {exporting ? 'Loading...' : 'Export'}
              </SociollaText>
            }
            onPress={() =>
              onExport().then(() => {
                setFilterExport({
                  from_date: '',
                  to_date: '',
                  coa_taxes: '',
                  sales_team: '',
                });
                dispatch(
                  showPopup({
                    name: 'showSkpExport',
                    value: false,
                  }),
                );
              })
            }
          />
        </View>
      </ModalFull>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  skpItem: {
    width: '100%',
    paddingVertical: 20,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  skpItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

export default SkpInternalScreen;
